import {
  Button,
  Divider,
  Group,
  NumberFormatter,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArchive,
  IconBrandLinkedin,
  IconMessage,
  IconPlayerPause,
  IconRocket,
  IconSparkles,
  IconUsersGroup,
} from "@tabler/icons-react";
import TypeSummary from "./TypeSummary";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useReportingStore } from "../../../../stores/ReportingStore";
import LoaderStats from "../LoaderStats";
import ReportingInfo from "../ReportingInfo";
import { IconEmail } from "../../../../assets/icons/IconEmail";
import { useLanguage } from "../../../../stores/LanguageStore";

function sumNumericValues(dataArray) {
  return dataArray.reduce((acc, obj) => {
    if (Array.isArray(obj.stats)) {
      obj.stats.forEach((stat) => {
        for (const key in stat) {
          if (typeof stat[key] === "number") {
            acc[key] = (acc[key] || 0) + stat[key];
          }
        }
      });
    }
    return acc;
  }, {});
}

const ReportingSummary = ({ inHome }) => {
  const lang = useLanguage((s) => s.language);
  const integrations = useReportingStore((s) => s.integrations);
  const programmations = useReportingStore((s) => s.programmations);
  const progStats = useReportingStore((s) => s.progStats);
  const actualStats = useReportingStore((s) => s.actualStats);
  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);
  const dateRange = useReportingStore((s) => s.dateRange);
  const global_stats = useReportingStore((s) => s.globalStats);
  const [isGlobal, setIsGlobal] = useState(true);

  const isDate = useMemo(
    () =>
      (dateRange.start_date || dateRange.end_date) &&
      programmations.length === 0 &&
      integrations.length === 0,
    [
      dateRange.end_date,
      dateRange.start_date,
      integrations.length,
      programmations.length,
    ],
  );

  const isMulti = useMemo(() => programmations.length > 1, [programmations]);

  const statistics = useMemo(() => {
    if (isDate) {
      const totals = actualStatsDate.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
          if (key !== "date") {
            acc[key] = (acc[key] || 0) + curr[key];
          }
        });
        return acc;
      }, {});
      return totals;
    }

    if (isMulti) {
      return sumNumericValues(progStats);
    }

    return actualStats;
  }, [actualStats, actualStatsDate, isDate, isMulti, progStats]);

  const emailAnswerRate = useMemo(() => {
    let total = isGlobal
      ? isDate
        ? statistics.opener
        : statistics.opener_total
      : statistics.email_sent;
    let answer = statistics.email_answer;

    if (total) {
      return (answer / total) * 100;
    }
    return 0;
  }, [
    isGlobal,
    isDate,
    statistics.opener,
    statistics.opener_total,
    statistics.email_sent,
    statistics.email_answer,
  ]);

  const linkedinAnswerRate = useMemo(() => {
    if (
      statistics.linkedin_invitation_sent ||
      statistics.linkedin_message_sent
    ) {
      return (
        ((statistics.linkedin_invitation_accepted +
          statistics.linkedin_message_answered) /
          (statistics.linkedin_invitation_sent +
            statistics.linkedin_message_sent)) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_invitation_accepted,
    statistics.linkedin_invitation_sent,
    statistics.linkedin_message_answered,
    statistics.linkedin_message_sent,
  ]);

  const smsAnswerRate = useMemo(() => {
    if (statistics.sms_sent) {
      return (statistics.sms_answer / statistics.sms_sent) * 100;
    }
    return 0;
  }, [statistics.sms_sent, statistics.sms_answer]);  

  return (
    <Group align="stretch" wrap="nowrap" gap={"xl"}>
      <Stack flex={1}>
        {!inHome && (
          <Title order={3} fw={500} ff={"Inter"} mb={8} pos={"relative"}>
            <span style={{ fontSize: 24, marginRight: 8 }}>📋</span>{" "}
            {lang.reports.campaign_summary}
          </Title>
        )}

        <Group w={"100%"} wrap="nowrap" align="stretch" gap={48}>
          <Stack flex={1} gap={"lg"}>
            <Paper
              component={Group}
              w={"100%"}
              gap={"sm"}
              pos={"relative"}
              style={{ boxShadow: "#00000016 0px 1px 4px" }}
              p={"md"}
              px={"lg"}
              wrap="nowrap"
            >
              <LoaderStats isDate isProgStats borderRadius={12} />
              <ThemeIcon variant="filled" size={"xl"} radius={"md"}>
                <IconUsersGroup size={28} />
              </ThemeIcon>
              <Text size="xl" fw={600}>
                {lang.reports.global_audience}
              </Text>
              <ReportingInfo>
                <Text>{lang.reports.global_audience_text}</Text>
              </ReportingInfo>

              <Text fz={42} fw={600} ml={"auto"}>
                <NumberFormatter
                  value={
                    isDate
                      ? statistics?.sent || 0
                      : statistics?.contacts_unique || 0
                  }
                />
              </Text>
            </Paper>
            <Group gap={24} wrap="nowrap">
              <TypeSummary
                icon={<IconEmail size={24} />}
                title={lang.reports.emails}
                sent={statistics.email_sent}
                rate={emailAnswerRate}
                color={"var(--mantine-color-secondary-5)"}
                isGlobal={isGlobal}
                setIsGlobal={setIsGlobal}
                info={<Text>{lang.reports.global_email_text}</Text>}
                withOpener
                opens={
                  isDate
                    ? statistics.opener
                    : statistics?.email_opener_unique ||
                      statistics.opener_unique
                }
                withClicks
                clicks={isDate ? statistics.clicker : statistics.clicker_unique}
                answers={
                  statistics?.email_answer_unique || statistics.email_answer
                }
                withRate
                isUnique={!isDate}
              />

              <TypeSummary
                icon={<IconBrandLinkedin size={24} />}
                title={"LinkedIn"}
                sent={
                  statistics.linkedin_invitation_sent +
                  statistics.linkedin_message_sent
                }
                answers={
                  statistics?.linkedin_message_answered_unique ||
                  statistics.linkedin_message_answered
                }
                rate={linkedinAnswerRate}
                color={"#4895ff"}
                info={<Text>{lang.reports.global_linkedin_text}</Text>}
                withOpener
                opens={
                  statistics?.linkedin_invitation_accepted_unique ||
                  statistics.linkedin_invitation_accepted
                }
                isLinkedin
                withRate
                isUnique={!isDate}
              />

              <TypeSummary
                icon={<IconMessage size={24} />}
                title={"SMS"}
                sent={statistics.sms_sent}
                rate={smsAnswerRate}
                answers={statistics.sms_answer}
                withRate={true}
                color={"green.6"}
                info={<Text>{lang.reports.global_sms_text}</Text>}
                isUnique={!isDate}
              />
            </Group>
          </Stack>

          <Paper
            component={Stack}
            p={"md"}
            justify="center"
            gap={"xs"}
            pos={"relative"}
            flex={0.3}
            style={{ boxShadow: "#00000016 0px 1px 4px" }}
          >
            <ReportingInfo pos={"absolute"} top={8} right={8}>
              <Text>{lang.reports.campaigns_status_text}</Text>
            </ReportingInfo>
            <Group
              fz={32}
              align="start"
              bg={"#fff"}
              p={8}
              style={{ borderRadius: 12 }}
            >
              <Stack gap={4} w={175}>
                <Group c={"var(--mantine-color-primary-6)"}>
                  <ThemeIcon variant="subtle" size={48} color="primary.6">
                    <IconRocket size={48} />
                  </ThemeIcon>

                  <NumberFormatter
                    style={{ fontWeight: 600 }}
                    value={global_stats.programmation_running}
                  />
                </Group>
                <Text size="lg" c={"black"} fw={400}>
                  {lang.reports.active_campaigns}
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Group
              fz={32}
              align="start"
              bg={"#fff"}
              p={8}
              style={{ borderRadius: 12 }}
            >
              <Stack gap={4} w={175}>
                <Group c={"var(--mantine-color-primary-6)"}>
                  <ThemeIcon variant="subtle" size={48} color="primary.6">
                    <IconPlayerPause size={48} />
                  </ThemeIcon>

                  <NumberFormatter
                    style={{ fontWeight: 600 }}
                    value={global_stats.programmation_stopped}
                  />
                </Group>
                <Text size="lg" c={"black"} fw={400}>
                  {lang.reports.paused_campaigns}
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Group
              fz={32}
              align="start"
              bg={"#fff"}
              p={8}
              style={{ borderRadius: 12 }}
            >
              <Stack gap={4} w={175}>
                <Group c={"var(--mantine-color-primary-6)"}>
                  <ThemeIcon variant="subtle" size={48} color="primary.6">
                    <IconArchive size={48} />
                  </ThemeIcon>

                  <NumberFormatter
                    style={{ fontWeight: 600 }}
                    value={global_stats.programmation_archived}
                  />
                </Group>
                <Text size="lg" c={"black"} fw={400}>
                  {lang.reports.archived_campaigns}
                </Text>
              </Stack>
            </Group>

            <Group mt={16}>
              <Button
                leftSection={<IconSparkles fill="#fff" />}
                size="md"
                w={"100%"}
                component={Link}
                to={"/campaign/create"}
                color="primary"
              >
                {lang.reports.create_campaign}
              </Button>
            </Group>
          </Paper>
        </Group>
      </Stack>
    </Group>
  );
};

export default ReportingSummary;
